import { useNavigate } from 'react-router-dom'
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { Person, School, Sports } from '@mui/icons-material'
import { useAthleteProfileForm } from '../../../hooks/useAthleteProfileForm'
import { ProfileSection } from '../ProfileSection'
import { PersonalInfoFields } from './PersonalInfoFields'
import { AcademicInfoFields } from './AcademicInfoFields'
import { AthleticInfoFields } from './AthleticInfoFields'

/**
 * Single-page edit form: every field on one scroll, no stepper. Visibility
 * (`isPublic`) is never touched here — it's owned solely by Settings, so a
 * routine edit can never accidentally publish or unpublish a profile.
 */
export const AthleteProfileEditForm = () => {
  const navigate = useNavigate()
  const form = useAthleteProfileForm()

  const handleSave = async () => {
    const ok = await form.save()
    if (ok) {
      setTimeout(() => navigate('/profile'), 600)
    } else {
      setTimeout(() => {
        document.querySelector('[aria-invalid="true"]')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 50)
    }
  }

  if (form.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Edit profile
      </Typography>

      {form.saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Profile saved.
        </Alert>
      )}
      {form.isSaveHttpError && !form.saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to save profile. Please try again.
        </Alert>
      )}
      {form.saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {form.saveError}
        </Alert>
      )}

      <Stack spacing={3}>
        <ProfileSection title="Personal information" icon={<Person />}>
          <PersonalInfoFields form={form} />
        </ProfileSection>

        <ProfileSection title="Academic information" icon={<School />}>
          <AcademicInfoFields form={form} />
        </ProfileSection>

        <ProfileSection title="Athletic data" icon={<Sports />}>
          <AthleticInfoFields form={form} />
        </ProfileSection>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => navigate('/profile')} disabled={form.isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={form.isSaving}>
            {form.isSaving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}
